import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { tourFor, type Tour, type TourStep } from '../lib/tours';

/**
 * A guided tour that makes the person do the thing.
 *
 * The spotlight is a ring with an enormous shadow rather than a full-screen
 * blocker, so everything stays clickable underneath. That is deliberate: a
 * tour that traps you is a tour people learn to dismiss, and a step that says
 * "click New ticket" only teaches anything if clicking New ticket is possible.
 *
 * Which tour somebody gets is decided by their role, and which steps by their
 * permissions - see lib/tours.ts.
 */

interface TourValue {
  /** Null when this role has no tour, so Help can hide the entry. */
  available: Tour | null;
  running: boolean;
  start: () => void;
  stop: () => void;
}

const TourContext = createContext<TourValue | null>(null);

/** Remembers only that it has been seen, so it never opens itself twice. */
const seenKey = (tourId: string) => `sarthi.tour.seen.${tourId}`;

const wasSeen = (tourId: string): boolean => {
  try {
    return localStorage.getItem(seenKey(tourId)) === '1';
  } catch {
    // Storage blocked: better to skip the automatic opening than to risk
    // showing it on every single page load.
    return true;
  }
};

const markSeen = (tourId: string) => {
  try {
    localStorage.setItem(seenKey(tourId), '1');
  } catch {
    /* nothing to do; it simply offers itself again next time */
  }
};

export function TourProvider({ children }: { children: ReactNode }) {
  const { session, subscription } = useAuth();
  const [running, setRunning] = useState(false);

  const available = useMemo(
    () => tourFor(session?.role ?? null, session?.permissions ?? []),
    [session?.role, session?.permissions],
  );

  const start = useCallback(() => setRunning(true), []);
  const stop = useCallback(() => {
    setRunning(false);
    if (available) markSeen(available.id);
  }, [available]);

  /*
   * Offers itself once, and never over something else.
   *
   * A tour that opens on top of the activate-your-plan prompt, or a modal the
   * person is halfway through, is an interruption rather than an introduction.
   * Anything with role="dialog" on screen means wait for another day.
   */
  useEffect(() => {
    if (!available || running || wasSeen(available.id)) return;
    /*
     * And never to a hotel that cannot use the product yet. Somebody looking
     * at "pay for your plan" needs the way to pay, not a walk around screens
     * they are locked out of - and the tour card would land on top of the
     * button that takes them there.
     */
    if (subscription && !subscription.isEntitled) return;

    const timer = setTimeout(() => {
      if (document.querySelector('[role="dialog"]')) return;
      setRunning(true);
    }, 1400);
    return () => clearTimeout(timer);
  }, [available, running, subscription]);

  const value = useMemo<TourValue>(
    () => ({ available, running, start, stop }),
    [available, running, start, stop],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {running && available ? <TourRunner tour={available} onDone={stop} /> : null}
    </TourContext.Provider>
  );
}

export function useTour(): TourValue {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTour must be used inside <TourProvider>');
  return value;
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Where the spotlight sits, or null while the target is not on screen. */
function useTargetBox(selector: string | undefined, step: number): Box | null {
  const [box, setBox] = useState<Box | null>(null);

  useEffect(() => {
    if (!selector) {
      setBox(null);
      return;
    }

    /*
     * Polled rather than watched. The target may not exist yet - a route is
     * still loading, a panel is still opening - and it moves when the page
     * scrolls or the window resizes. One cheap measurement a few times a
     * second covers all of it without a stack of listeners and observers.
     */
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const element = document.querySelector(selector);
      if (!element) {
        setBox(null);
        return;
      }
      const rect = element.getBoundingClientRect();
      // Only when it has actually moved. Setting a fresh object every tick
      // re-rendered the overlay four times a second for no reason, and left
      // nothing on the page ever looking still.
      setBox((was) =>
        was &&
        was.top === rect.top &&
        was.left === rect.left &&
        was.width === rect.width &&
        was.height === rect.height
          ? was
          : { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      );
    };

    measure();
    const timer = setInterval(measure, 250);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selector, step]);

  return box;
}

const CARD_W = 360;
const CARD_H = 210;

/**
 * Where to put the card so it does not cover what it is pointing at.
 *
 * Beside the target when it is something down the left - the sidebar, where
 * most steps point - because sitting underneath one nav item means sitting on
 * top of the next four. Below it otherwise, and above when the bottom of the
 * screen is closer. No target at all is a plain card in the middle.
 */
function placeCard(box: Box | null): React.CSSProperties {
  if (!box) {
    return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const clamp = (value: number, max: number) => Math.min(Math.max(12, value), Math.max(12, max));
  const beside = box.left + box.width + 16;

  if (beside + CARD_W < window.innerWidth && box.left < window.innerWidth * 0.34) {
    return {
      position: 'fixed',
      left: beside,
      top: clamp(box.top + box.height / 2 - CARD_H / 2, window.innerHeight - CARD_H - 12),
    };
  }

  const below = box.top + box.height + 12;
  const above = below + CARD_H > window.innerHeight;
  return {
    position: 'fixed',
    top: above ? Math.max(12, box.top - 12) : below,
    left: clamp(box.left, window.innerWidth - CARD_W - 12),
    transform: above ? 'translateY(-100%)' : undefined,
  };
}

function TourRunner({ tour, onDone }: { tour: Tour; onDone: () => void }) {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const step: TourStep | undefined = tour.steps[index];
  const box = useTargetBox(step?.target, index);

  const next = useCallback(() => {
    setIndex((was) => {
      if (was + 1 >= tour.steps.length) {
        onDone();
        return was;
      }
      return was + 1;
    });
  }, [tour.steps.length, onDone]);

  /*
   * Take them to the screen the step is about, if they are not there.
   *
   * Keyed on the step alone. Watching the location too would yank them back
   * the moment they navigated away mid-step, which is a fight they cannot
   * win and not one worth having - leaving is allowed.
   */
  const route = step?.route;
  useEffect(() => {
    if (route && window.location.pathname !== route) navigate(route);
  }, [index, route, navigate]);

  /*
   * The interactive part: the step names what to do, and doing it moves on.
   *
   * Listened for in the capture phase so it still counts when the control
   * stops the event, and by containment rather than identity because the
   * thing clicked is usually a span inside the button.
   */
  useEffect(() => {
    const click = step?.action?.click ?? (step?.action && !step.action.waitFor && !step.action.route ? step.target : undefined);
    if (!click) return;

    const onClick = (event: MouseEvent) => {
      const target = document.querySelector(click);
      if (target && event.target instanceof Node && target.contains(event.target)) {
        // After the app has handled it, so the next step measures the screen
        // the click produced rather than the one before it.
        setTimeout(next, 350);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [step, next]);

  /* Or: the step is done when something appears. */
  useEffect(() => {
    const selector = step?.action?.waitFor;
    if (!selector) return;
    const timer = setInterval(() => {
      if (document.querySelector(selector)) next();
    }, 300);
    return () => clearInterval(timer);
  }, [step, next]);

  /* Or: when they arrive somewhere. */
  useEffect(() => {
    const to = step?.action?.route;
    if (to && location.pathname === to) {
      const timer = setTimeout(next, 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step, location.pathname, next]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDone();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDone]);

  if (!step) return null;

  const total = tour.steps.length;
  const percent = Math.round(((index + 1) / total) * 100);
  const waiting = Boolean(step.action);

  const card = placeCard(box);

  return createPortal(
    <div className="tour-layer">
      {box ? (
        <div
          className="tour-spot"
          style={{
            top: box.top - 6,
            left: box.left - 6,
            width: box.width + 12,
            height: box.height + 12,
          }}
        />
      ) : (
        <div className="tour-dim" />
      )}

      <div className="tour-card" style={card} role="dialog" aria-label={step.title}>
        <div className="tour-progress">
          <div className="tour-progress-bar" style={{ width: `${percent}%` }} />
        </div>

        <div className="tour-head">
          <span className="xs muted">
            Step {index + 1} of {total} · {tour.title}
          </span>
          <button className="btn ghost icon sm" onClick={onDone} aria-label="End tour">
            ✕
          </button>
        </div>

        <h3>{step.title}</h3>
        <p className="small">{step.body}</p>

        <div className="tour-foot">
          {waiting ? (
            <span className="tour-waiting">
              <span className="tour-pulse" aria-hidden="true" />
              {step.action!.label}
            </span>
          ) : (
            <span className="xs muted">
              {index === 0 ? tour.summary : 'Press Escape to leave at any time.'}
            </span>
          )}
          <span className="row gap-6">
            {index > 0 ? (
              <button className="btn sm" onClick={() => setIndex((was) => Math.max(0, was - 1))}>
                Back
              </button>
            ) : null}
            {/* Always offered, even on an interactive step: somebody who
                already knows the screen should not be made to perform. */}
            <button className="btn sm primary" onClick={next}>
              {index + 1 === total ? 'Finish' : waiting ? 'Skip' : 'Next'}
            </button>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
